// THE APP SPLIT — WS-STUDIO-SPLIT-WORKSPACE-APPS-FROM-SITES, docs/APP_SPLIT.md.
//
// Nika 2026-10-19: "strictly divide workspace apps from external apps that
// users build. they should not go to the same canvas. workspace apps are pure
// adons on workspace and websites made on it are deployed and published."
//
// A project is exactly ONE of four kinds, decided by WHERE it runs:
//
//   'workspaceApp' — an add-on that runs INSIDE a workspace: a manifest-2
//                    module (system/crm-app, a custom org module, the takeover
//                    module) or an AI-built app (metadata.extension). It is
//                    installed into a workspace and opened from its launcher.
//                    It is never published as a site. It lives on the
//                    Workspace apps page (/apps) and is built in /add-app or
//                    in code (apps/* + smbls push).
//   'website'      — everything built for people OUTSIDE the workspace: a
//                    site, a landing page, a web / mobile / desktop / TV app,
//                    a presentation. It is deployed and published. It lives
//                    on the Websites board (/studio) and is built in the
//                    canvas editor.
//   'library'      — a shared library / design system (a building block of
//                    websites; it renders on the Websites board as a source
//                    of arrows, never as a thing to publish or install).
//   'template'     — a starting point in the marketplace.
//
// EVERY surface that lists or opens projects asks THIS module — never its own
// guess (the old guess was "not installed here = a website", which turned an
// uninstalled system/bi-app into "our website").
//
// Signals, strongest first:
//   1. server markers on the project row — metadata.workspaceModule (catalog
//      or custom module), metadata.extension (AI-built app), metadata.appKind
//      (server-stamped multipage app) — and, on a LOADED project's content,
//      the module manifest (`manifest: 2`, top level or under config). The
//      project LIST endpoint does not return `metadata` yet (the server half
//      of the ticket adds it), so:
//   2. what this workspace knows (`ctx`, built by projectKindContext): the
//      installed app ids (owner/key — the plural list, the takeover module),
//      the AI-built app ids (project _id — the workspace's extension list and
//      every extension ref) and the App Store catalog ids (owner/key).
//
// `ctx` holds plain ARRAYS of strings (root state stores no Set):
//   { installed: ['system/crm-app', …], extensions: ['6a9…', …],
//     modules: ['system/bi-app', …] }
//
// FA204 / frank trap — every export below carries its own inline helpers;
// none reaches a sibling export or a module-local helper bare.

import { getAiService } from './aiService.js'

// el.call('projectKind', project, ctx) — 'workspaceApp' | 'website' |
// 'library' | 'template'. Accepts a raw project row (the SDK list shape) or a
// Studio board node (normalizeProject: `id` = 'owner/key', `_id` = db id).
export const projectKind = (project, ctx) => {
  const p = project && typeof project === 'object' ? project : {}
  const c = ctx && typeof ctx === 'object' ? ctx : {}
  const lc = (v) => (v == null ? '' : String(v).trim().toLowerCase())
  const isObjectId = (v) => typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v)
  const listHas = (list, v) => !!v && Array.isArray(list) && list.some((x) => lc(x) === v)
  const ownerOf = () => {
    const o = p.owner
    if (typeof o === 'string' && o && !isObjectId(o)) return o
    if (o && typeof o === 'object') {
      const s = o.slug || o.username || o.key
      if (s) return s
    }
    if (p.ownerUsername) return p.ownerUsername
    const org = p.workspace && typeof p.workspace === 'object' && p.workspace.organization
    if (org && typeof org === 'object' && org.slug) return org.slug
    // A board node's `id` is already 'owner/key'.
    if (typeof p.id === 'string' && p.id.indexOf('/') > 0) return p.id.split('/')[0]
    return ''
  }

  const type = lc(p.projectType)
  if (p.isSharedLibrary === true || type === 'library') return 'library'
  if (type === 'template') return 'template'

  const m = p.metadata && typeof p.metadata === 'object' ? p.metadata : {}
  if (m.workspaceModule === true || m.extension === true) return 'workspaceApp'
  if (typeof m.appKind === 'string' && m.appKind) return 'workspaceApp'
  // A loaded project's CONTENT (the editor's root.data) carries the module
  // manifest itself: manifest v2 at the top or under config.
  const cfg = p.config && typeof p.config === 'object' ? p.config : {}
  if (p.manifest === 2 || cfg.manifest === 2) return 'workspaceApp'

  const key = lc(String(p.key || '').split('.')[0])
  const owner = lc(ownerOf())
  const appId = owner && key ? owner + '/' + key : ''
  if (appId && (listHas(c.installed, appId) || listHas(c.modules, appId))) {
    return 'workspaceApp'
  }
  const dbId = lc(p._id || (isObjectId(p.id) ? p.id : ''))
  if (dbId && listHas(c.extensions, dbId)) return 'workspaceApp'
  return 'website'
}

// el.call('projectKindContext', root) — the workspace's own signals as plain
// arrays (see the header). Pure: reads root state only. Sources:
//   installed  — root.workspaceApps[].id (activated manifests), the takeover
//                module (root.workspaceProject.id), and the raw refs on the
//                workspace record (settings.workspaceApps + the legacy
//                settings.workspaceModule) so a not-yet-activated install
//                still counts;
//   extensions — extension refs ({ type: 'extension', projectId }), the
//                /add-app list (root._userApps) and the loaded signals;
//   modules    — the App Store catalog (root._wsAppsCatalog, loaded by
//                /add-app) and the loaded signals.
export const projectKindContext = (root) => {
  const r = root && typeof root === 'object' ? root : {}
  const lc = (v) => (v == null ? '' : String(v).trim().toLowerCase())
  const isObjectId = (v) => typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v)
  const installed = new Set()
  const extensions = new Set()
  const modules = new Set()
  const addAppId = (owner, key) => {
    if (owner && key) installed.add(lc(owner) + '/' + lc(key))
  }
  const wp = r.workspaceProject
  if (wp && typeof wp.id === 'string' && wp.id.indexOf('/') > 0) installed.add(lc(wp.id))
  for (const a of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    if (!a || typeof a.id !== 'string') continue
    // An AI-built app publishes its project _id as its id.
    if (isObjectId(a.id)) extensions.add(lc(a.id))
    else if (a.id.indexOf('/') > 0) installed.add(lc(a.id))
  }
  const ws = r.workspace && typeof r.workspace === 'object' ? r.workspace : {}
  const settings = ws.settings && typeof ws.settings === 'object' ? ws.settings : {}
  for (const ref of Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []) {
    if (!ref || typeof ref !== 'object') continue
    if (ref.type === 'extension') {
      if (ref.projectId) extensions.add(lc(ref.projectId))
      continue
    }
    if (!ref.type || ref.type === 'project') addAppId(ref.owner, ref.key)
  }
  const legacy = settings.workspaceModule
  if (legacy && typeof legacy === 'object') addAppId(legacy.owner, legacy.key)
  for (const x of Array.isArray(r._userApps) ? r._userApps : []) {
    const id = x && (x.id || x._id || x.projectId)
    if (id) extensions.add(lc(id))
  }
  for (const x of Array.isArray(r._wsAppsCatalog) ? r._wsAppsCatalog : []) {
    if (x && x.id && String(x.id).indexOf('/') > 0) modules.add(lc(x.id))
  }
  const sig =
    r._projectKindSignals && typeof r._projectKindSignals === 'object'
      ? r._projectKindSignals
      : null
  if (sig) {
    for (const id of Array.isArray(sig.extensions) ? sig.extensions : []) {
      if (id) extensions.add(lc(id))
    }
    for (const id of Array.isArray(sig.modules) ? sig.modules : []) {
      if (id) modules.add(lc(id))
    }
  }
  return { installed: [...installed], extensions: [...extensions], modules: [...modules] }
}

// el.call('splitProjectsByKind', list, ctx) — one pass over a project list:
// `{ websites, workspaceApps, libraries, templates }`, each in list order.
export const splitProjectsByKind = (list, ctx) => {
  const out = { websites: [], workspaceApps: [], libraries: [], templates: [] }
  const c = ctx && typeof ctx === 'object' ? ctx : {}
  const lc = (v) => (v == null ? '' : String(v).trim().toLowerCase())
  const isObjectId = (v) => typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v)
  const listHas = (arr, v) => !!v && Array.isArray(arr) && arr.some((x) => lc(x) === v)
  // Inline copy of projectKind (FA204 — no bare sibling reach).
  const kindOf = (project) => {
    const p = project && typeof project === 'object' ? project : {}
    const type = lc(p.projectType)
    if (p.isSharedLibrary === true || type === 'library') return 'library'
    if (type === 'template') return 'template'
    const m = p.metadata && typeof p.metadata === 'object' ? p.metadata : {}
    if (m.workspaceModule === true || m.extension === true) return 'workspaceApp'
    if (typeof m.appKind === 'string' && m.appKind) return 'workspaceApp'
    const cfg = p.config && typeof p.config === 'object' ? p.config : {}
    if (p.manifest === 2 || cfg.manifest === 2) return 'workspaceApp'
    let owner = ''
    const o = p.owner
    if (typeof o === 'string' && o && !isObjectId(o)) owner = o
    else if (o && typeof o === 'object') owner = o.slug || o.username || o.key || ''
    if (!owner && p.ownerUsername) owner = p.ownerUsername
    if (!owner) {
      const org = p.workspace && typeof p.workspace === 'object' && p.workspace.organization
      if (org && typeof org === 'object' && org.slug) owner = org.slug
    }
    if (!owner && typeof p.id === 'string' && p.id.indexOf('/') > 0) owner = p.id.split('/')[0]
    const key = lc(String(p.key || '').split('.')[0])
    const appId = owner && key ? lc(owner) + '/' + key : ''
    if (appId && (listHas(c.installed, appId) || listHas(c.modules, appId))) return 'workspaceApp'
    const dbId = lc(p._id || (isObjectId(p.id) ? p.id : ''))
    if (dbId && listHas(c.extensions, dbId)) return 'workspaceApp'
    return 'website'
  }
  const bucket = {
    website: 'websites',
    workspaceApp: 'workspaceApps',
    library: 'libraries',
    template: 'templates'
  }
  for (const p of Array.isArray(list) ? list : []) {
    if (!p) continue
    out[bucket[kindOf(p)]].push(p)
  }
  return out
}

// el.call('loadProjectKindSignals', { force }) — fetch the two lists the
// project LIST endpoint cannot tell yet (until the server returns `metadata`
// on list rows) and keep them on root._projectKindSignals for the ACTIVE
// workspace:
//   extensions — the workspace's AI-built apps (sdk ai.listExtensions);
//   modules    — the App Store catalog (sdk.listPublicProjects with
//                workspaceModule: true), as owner/key.
// One load per workspace and per five minutes; a failed read keeps the
// previous lists (a stale list only means a later, smaller split — it never
// moves a website onto the apps page). SDK-only backend access.
export async function loadProjectKindSignals(opts) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return null
  const force = !!(opts && opts.force)
  const wsId = String(
    (root.workspace && (root.workspace.id || root.workspace._id)) ||
      (root.jwtClaims && root.jwtClaims.active_workspace_id) ||
      ''
  )
  if (!wsId) return null
  const prev =
    root._projectKindSignals && typeof root._projectKindSignals === 'object'
      ? root._projectKindSignals
      : null
  const FRESH_MS = 5 * 60 * 1000
  if (!force && prev && prev.wsId === wsId && Date.now() - (prev.at || 0) < FRESH_MS) return prev
  // One read in flight per element tree (el.scope is per element; the shell
  // root is the shared owner of this read).
  const holder = root.__projectKindSignalsInFlight
  if (holder && holder.wsId === wsId) return holder.promise
  const sdk = el.sdk || (el.context && el.context.methods && el.context.methods.sdk)
  const lc = (v) => (v == null ? '' : String(v).trim().toLowerCase())
  const readExtensions = async () => {
    const ai = getAiService()
    if (!ai || typeof ai.listExtensions !== 'function') return null
    const res = await ai.listExtensions({ workspaceId: wsId })
    const arr = Array.isArray(res) ? res : (res && (res.data || res.items)) || []
    return arr
      .map((x) => x && (x.id || x._id || x.projectId))
      .filter(Boolean)
      .map(lc)
  }
  const readModules = async () => {
    if (!sdk || typeof sdk.listPublicProjects !== 'function') return null
    const res = await sdk.listPublicProjects({ workspaceModule: true, limit: 100 })
    const rows = Array.isArray(res) ? res : (res && res.data) || []
    return rows
      .filter((p) => p && p.metadata && p.metadata.workspaceModule === true && p.key)
      .map((p) => {
        const owner =
          (p.workspace && p.workspace.organization && p.workspace.organization.slug) ||
          (p.workspace && p.workspace.slug) ||
          (typeof p.owner === 'string' ? p.owner : p.owner && (p.owner.slug || p.owner.username)) ||
          ''
        return owner ? lc(owner) + '/' + lc(p.key) : ''
      })
      .filter(Boolean)
  }
  const promise = (async () => {
    const [ext, mods] = await Promise.all([
      readExtensions().catch(() => null),
      readModules().catch(() => null)
    ])
    // A switch while the reads were in flight: the answer belongs to the old
    // workspace — drop it.
    const nowWs = String(
      (root.workspace && (root.workspace.id || root.workspace._id)) ||
        (root.jwtClaims && root.jwtClaims.active_workspace_id) ||
        ''
    )
    if (nowWs !== wsId) return null
    const same = prev && prev.wsId === wsId
    const next = {
      wsId,
      extensions: ext || (same ? prev.extensions || [] : []),
      modules: mods || (same ? prev.modules || [] : []),
      at: Date.now()
    }
    root.update({ _projectKindSignals: next })
    return next
  })()
  root.__projectKindSignalsInFlight = { wsId, promise }
  try {
    return await promise
  } finally {
    if (
      root.__projectKindSignalsInFlight &&
      root.__projectKindSignalsInFlight.promise === promise
    ) {
      root.__projectKindSignalsInFlight = null
    }
  }
}
